import React, { useState, useEffect } from 'react'
import { 
  WeatherAlert, 
  EmergencyTask, 
  createWeatherAlert, 
  getSeverityColor, 
  getSeverityLabel,
  WeatherAlertType,
  alertTemplates
} from '../data/weatherAlerts'

interface WeatherAlertsProps {
  zipCode?: string
  lastFrost?: Date | null
}

const WeatherAlerts: React.FC<WeatherAlertsProps> = ({ zipCode, lastFrost }) => {
  const [alerts, setAlerts] = useState<WeatherAlert[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedAlertType, setSelectedAlertType] = useState<WeatherAlertType>('frost')
  const [temperature, setTemperature] = useState('')
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null)

  // Load alerts from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('garden_weather_alerts')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        // Convert date strings back to Date objects
        const alertsWithDates = parsed.map((alert: any) => ({
          ...alert,
          date: new Date(alert.date),
          tasks: alert.tasks.map((task: any) => ({
            ...task,
            completed: task.completed || false
          }))
        }))
        setAlerts(alertsWithDates)
      } catch (e) {
        console.error('Failed to load alerts:', e)
      }
    }
  }, [])

  // Save alerts to localStorage
  useEffect(() => {
    localStorage.setItem('garden_weather_alerts', JSON.stringify(alerts))
  }, [alerts])

  const createAlert = () => {
    const temp = temperature ? parseInt(temperature) : undefined
    const newAlert = createWeatherAlert(selectedAlertType, temp)
    setAlerts(prev => [newAlert, ...prev])
    setShowCreateModal(false)
    setTemperature('')
  }

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId))
  }

  const toggleTaskComplete = (alertId: string, taskId: string) => {
    setAlerts(prev => prev.map(alert => {
      if (alert.id !== alertId) return alert
      return {
        ...alert,
        tasks: alert.tasks.map(task => 
          task.id === taskId ? { ...task, completed: !task.completed } : task
        )
      }
    }))
  }

  const getCompletionPercentage = (alert: WeatherAlert): number => {
    if (alert.tasks.length === 0) return 0
    const completed = alert.tasks.filter(t => t.completed).length
    return Math.round((completed / alert.tasks.length) * 100)
  }

  const getPriorityLabel = (priority: EmergencyTask['priority']) => {
    switch (priority) {
      case 'urgent': return '🔴 Urgent'
      case 'important': return '🟡 Important'
      case 'recommended': return '🟢 Recommended'
    }
  }

  const activeAlerts = alerts.filter(a => {
    // Keep alerts from last 3 days
    const threeDaysAgo = new Date()
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)
    return a.date >= threeDaysAgo
  })

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3>🌩️ Weather Alerts</h3>
            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
              Emergency tasks to protect your garden from severe weather
            </p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Alert
          </button>
        </div>
      </div>

      {/* Active Alerts */}
      {activeAlerts.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>☀️</div>
          <h4>No Active Alerts</h4>
          <p style={{ color: '#666', marginTop: '0.5rem' }}>
            Weather looks good! Create an alert if severe weather is forecast.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {activeAlerts.map(alert => {
            const completion = getCompletionPercentage(alert)
            const isExpanded = expandedAlert === alert.id
            
            return (
              <div 
                key={alert.id} 
                className="card"
                style={{ 
                  borderLeft: `4px solid ${getSeverityColor(alert.severity)}`,
                  background: completion === 100 ? '#e8f5e9' : 'white'
                }}
              >
                {/* Alert Header */}
                <div 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpandedAlert(isExpanded ? null : alert.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h4 style={{ margin: 0 }}>{alert.title}</h4>
                      <span 
                        style={{ 
                          padding: '0.25rem 0.5rem',
                          background: getSeverityColor(alert.severity),
                          color: 'white',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        {getSeverityLabel(alert.severity)}
                      </span>
                      {completion === 100 && (
                        <span style={{ color: '#4caf50', fontSize: '0.9rem' }}>
                          ✓ All Tasks Complete
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
                      {alert.description}
                    </p>
                    <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                      {alert.date.toLocaleDateString()} • {alert.tasks.length} tasks • {completion}% complete
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedAlert(isExpanded ? null : alert.id)
                      }}
                    >
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', color: '#d9534f' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        dismissAlert(alert.id)
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    background: '#eee',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${completion}%`,
                      height: '100%',
                      background: completion === 100 ? '#4caf50' : getSeverityColor(alert.severity),
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {/* Tasks List */}
                {isExpanded && (
                  <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                    <h5 style={{ marginBottom: '0.75rem' }}>Emergency Tasks</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {alert.tasks.map(task => (
                        <div 
                          key={task.id}
                          style={{
                            padding: '0.75rem',
                            background: task.completed ? '#e8f5e9' : '#f9f9f9',
                            borderRadius: '8px',
                            border: `1px solid ${task.completed ? '#4caf50' : '#ddd'}`
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                            <input
                              type="checkbox"
                              checked={task.completed}
                              onChange={() => toggleTaskComplete(alert.id, task.id)}
                              style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                            />
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontWeight: 500,
                                textDecoration: task.completed ? 'line-through' : 'none',
                                color: task.completed ? '#666' : '#333'
                              }}>
                                {task.task}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                {getPriorityLabel(task.priority)}
                                {task.timeEstimate && ` • ${task.timeEstimate}`}
                              </div>
                              <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.5rem' }}>
                                {task.instructions}
                              </div>
                              {task.materials && task.materials.length > 0 && (
                                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                                  <strong>Materials needed:</strong> {task.materials.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create Alert Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h3>Create Weather Alert</h3>
            
            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem' }}>Alert Type</label>
              <select 
                value={selectedAlertType}
                onChange={(e) => setSelectedAlertType(e.target.value as WeatherAlertType)}
                style={{ width: '100%', padding: '0.5rem' }}
              >
                <option value="frost">🥶 Frost Warning (32-35°F)</option>
                <option value="freeze">❄️ Hard Freeze (below 28°F)</option>
                <option value="heat-wave">🌡️ Extreme Heat (90°F+)</option>
                <option value="drought">💧 Drought Conditions</option>
                <option value="storm">⛈️ Severe Storm</option>
                <option value="high-wind">💨 High Winds (25+ mph)</option>
                <option value="hail">🧊 Hail Expected</option>
                <option value="flood">🌊 Flood Warning</option>
              </select>
            </div>

            {(selectedAlertType === 'frost' || selectedAlertType === 'freeze' || selectedAlertType === 'heat-wave') && (
              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem' }}>
                  Expected Temperature (°F)
                </label>
                <input
                  type="number"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder={selectedAlertType === 'heat-wave' ? 'e.g., 95' : 'e.g., 30'}
                  style={{ width: '100%', padding: '0.5rem' }}
                />
              </div>
            )}

            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <strong>Preview:</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                {alertTemplates[selectedAlertType](temperature ? parseInt(temperature) : undefined).description}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button 
                className="btn btn-primary"
                onClick={createAlert}
                style={{ flex: 1 }}
              >
                Create Alert
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WeatherAlerts
